import {
  type LocationDetails,
  useFlightLoadingDetails,
  type UseFlightLoadingDetailsParams,
} from "@apac/hooks";
import { TripCategory } from "@b2bportal/core-types";
import {
  FlightSelectors,
  useCheckoutStateSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { AirportMap } from "@hopper-b2b/types";
import { DateTimeFormatStyle } from "@hopper-i18n/formatter";
import dayjs from "dayjs";

interface UseTripLoadingDetailsReturn {
  originDetails: LocationDetails;
  destinationDetails: LocationDetails;
  tripDate: string;
  passengerCounts: string;
}
const DEFAULT_DATE_FORMAT = DateTimeFormatStyle.Custom({
  year: "numeric",
  month: "short",
  day: "2-digit",
});
const DEFAULT_SEPARATOR = " - ";
const getLocationDetails = (
  location: { label: string; code: string },
  airportMap: AirportMap = {}
): { title: string; subtitle: string } => {
  if (!location || !location.label || !location.code) {
    return { title: "", subtitle: "" };
  }
  const { code, label } = location;

  const { cityName = "", geography } = airportMap?.[code] || {};
  const { countryCode = "" } = geography || {};

  const subtitle =
    cityName && countryCode ? `${cityName} • ${countryCode}` : "";

  return {
    title: label || "",
    subtitle,
  };
};

const getTripDate = (
  formatDateTime: (date: Date) => string,
  departureDate: string,
  returnDate: string,
  tripCategory: TripCategory,
  separator: string
) => {
  const dates = [formatDateTime(dayjs(departureDate).toDate())];

  if (tripCategory === TripCategory.ROUND_TRIP) {
    dates.push(formatDateTime(dayjs(returnDate).toDate()));
  }

  return dates.join(separator);
};

export const useTripLoadingDetails = (
  params?: UseFlightLoadingDetailsParams
): UseTripLoadingDetailsReturn => {
  const { formatDateTime } = useI18nContext();
  const { dateStringSeparator, dateFormatStyle } = params ?? {};
  const {
    context: {
      originDetails: flightShopOrigin,
      destinationDetails: flightShopDestination,
      tripDate: flightShopTripDate,
      passengerCounts,
    },
  } = useFlightLoadingDetails(params);

  const tripDetails = useCheckoutStateSelector(FlightSelectors.getTripDetails);
  let checkoutOrigin;
  let checkoutDestination;
  let checkoutTripDate;
  if (tripDetails) {
    const originSlice = tripDetails.slices[0];
    const destinationSlice = tripDetails.slices[tripDetails.slices.length - 1];
    const origin = {
      code: originSlice.originCode,
      label: originSlice.originName,
    };

    const destination = {
      code:
        tripDetails.slices.length === 1
          ? originSlice.destinationCode
          : destinationSlice.originCode,
      label:
        tripDetails.slices.length === 1
          ? originSlice.destinationName
          : destinationSlice.originName,
    };

    const airportMap = tripDetails.airports as unknown as AirportMap;
    checkoutOrigin = getLocationDetails(origin, airportMap);
    checkoutDestination = getLocationDetails(destination, airportMap);
    checkoutTripDate = getTripDate(
      (date: Date) =>
        formatDateTime(date, dateFormatStyle ?? DEFAULT_DATE_FORMAT),
      originSlice.departureTime,
      destinationSlice.departureTime,
      tripDetails.slices.length > 1
        ? TripCategory.ROUND_TRIP
        : TripCategory.ONE_WAY,
      dateStringSeparator ?? DEFAULT_SEPARATOR
    );
  }
  const originDetails =
    flightShopOrigin.title && flightShopOrigin.subtitle
      ? flightShopOrigin
      : checkoutOrigin;

  const destinationDetails =
    flightShopDestination.title && flightShopDestination.subtitle
      ? flightShopDestination
      : checkoutDestination;

  const tripDate = flightShopTripDate || checkoutTripDate;

  return {
    originDetails,
    destinationDetails,
    tripDate,
    passengerCounts,
  };
};
